import React, { useState, useEffect } from 'react'
import styled from 'styled-components'

interface TimerProps {
  duration?: number // Duration in seconds
  onTimeUp?: () => void // Callback when timer reaches 0
}

const TimerContainer = styled.div`
  width: 100%;
  max-width: 390px;
  margin: 0 auto;

  @media (min-width: 1440px) {
    margin-bottom: 0;
  }
`

const TimerDisplay = styled.div`
  text-align: center;
  color: white;
  font-size: 1.25rem;
  font-weight: bold;
  margin-bottom: 8px;

  .time-left {
    font-size: 1rem;
    color: #ffeb65;
    gap: 4px;
  }

  &.warning {
    .time-left {
      color: #ff6b6b;
      animation: pulse 1s infinite;
    }
  }

  @keyframes pulse {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0.6;
    }
    100% {
      opacity: 1;
    }
  }
`

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 8px;
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
`

const ProgressBar = styled.div<{ percentage: number; isWarning: boolean }>`
  height: 100%;
  width: ${(props) => props.percentage}%;
  background: ${(props) => (props.isWarning ? 'linear-gradient(90deg, #f34444ff 0%, #ef0c0cff 100%)' : '#ffeb65')};
  transition: width 1s linear;
  border-radius: 8px;
`

export default function Timer({ duration = 20, onTimeUp }: TimerProps) {
  const [timeLeft, setTimeLeft] = useState(duration)
  const [isActive, setIsActive] = useState(true)

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null

    if (isActive && timeLeft > 0) {
      intervalId = setInterval(() => {
        setTimeLeft((time) => {
          if (time <= 1) {
            setIsActive(false)
            if (onTimeUp) {
              onTimeUp()
            }
            return 0
          }
          return time - 1
        })
      }, 1000)
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, [isActive, timeLeft, onTimeUp])

  const percentage = (timeLeft / duration) * 100
  const isWarning = timeLeft <= 5

  const formatTime = (seconds: number) => {
    return `${seconds}s`
  }

  return (
    <TimerContainer>
      <TimerDisplay className={isWarning ? 'warning' : ''}>
        <div className="time-left d-flex align-items-center justify-content-center gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path
              d="M9.00008 0.666626C13.6026 0.666626 17.3334 4.39746 17.3334 8.99996C17.3334 13.6025 13.6026 17.3333 9.00008 17.3333C4.39758 17.3333 0.666748 13.6025 0.666748 8.99996C0.666748 4.39746 4.39758 0.666626 9.00008 0.666626ZM9.00008 2.33329C7.23197 2.33329 5.53628 3.03567 4.28604 4.28591C3.03579 5.53616 2.33341 7.23185 2.33341 8.99996C2.33341 10.7681 3.03579 12.4638 4.28604 13.714C5.53628 14.9642 7.23197 15.6666 9.00008 15.6666C10.7682 15.6666 12.4639 14.9642 13.7141 13.714C14.9644 12.4638 15.6667 10.7681 15.6667 8.99996C15.6667 7.23185 14.9644 5.53616 13.7141 4.28591C12.4639 3.03567 10.7682 2.33329 9.00008 2.33329ZM9.00008 3.99996C9.20419 3.99999 9.40119 4.07492 9.55372 4.21055C9.70625 4.34619 9.8037 4.53308 9.82758 4.73579L9.83341 4.83329V8.65496L12.0892 10.9108C12.2387 11.0608 12.3255 11.262 12.3319 11.4736C12.3384 11.6852 12.2641 11.8914 12.124 12.0502C11.984 12.209 11.7888 12.3085 11.578 12.3286C11.3672 12.3487 11.1567 12.2878 10.9892 12.1583L10.9109 12.0891L8.41091 9.58912C8.2814 9.4595 8.19822 9.29079 8.17425 9.10912L8.16675 8.99996V4.83329C8.16675 4.61228 8.25454 4.40032 8.41082 4.24404C8.5671 4.08776 8.77907 3.99996 9.00008 3.99996Z"
              fill="currentColor"
            />
          </svg>

          <span>{formatTime(timeLeft)}</span>
        </div>
      </TimerDisplay>
      <ProgressBarContainer>
        <ProgressBar percentage={percentage} isWarning={isWarning} />
      </ProgressBarContainer>
    </TimerContainer>
  )
}
